/**
 * Ayarlar on the phone (artboard F_Ayarlar): Diğer › Ayarlar. The page is also the setup
 * checklist ("Kurulum: 1 / 19 bağlantı hazır", the next step), then Bağlantılar, Politika,
 * Sert sınırlar and Tercihler, and a title-block grid of the other sections. Sub-screens
 * (Bütçeler, Onay süreleri, Bildirimler, Dil ve bölge, Değişiklik kaydı ...) push from here.
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { formatPolicyValue } from '../api/policy-rules.js';
import type { Connection, ConnectionGroupId, Settings } from '../api/settings.js';
import { usePanel, useStore } from '../app/context.js';
import { Link, type SettingsTab } from '../app/router.js';
import {
  agentsSummary,
  backupMissing,
  type GroupSummary,
  groupSummaries,
  isLive,
  killSwitchUntested,
  pendingSuggestions,
  policyById,
  type SetupSummary,
  STATUS_LABELS,
  type SummaryPart,
  searchSettings,
  setupSummary,
  TAB_TITLES,
} from '../app/settings.js';
import { ApiUsageBody } from '../components/api-usage.js';
import { Icon } from '../components/icons.js';
import { TriangleGlyph } from '../components/primitives.js';
import {
  ConnectionPill,
  HardLimits,
  PrefSwitch,
  Rich,
  richToString,
  SectionToggle,
  ServiceGlyph,
  SetupBar,
  SetupLegend,
} from '../components/settings.js';
import { BottomTabBar, DetailAppBar, StatusArea } from '../components/shell.js';
import {
  AboutBody,
  AgentsBody,
  ApprovalTimesBody,
  BudgetsBody,
  ChangeLogBody,
  DataBody,
  FlagsBlock,
  LanguageBody,
  MailStageRow,
  NotificationsBody,
  PolicyBody,
  PolicyRow,
  PreferencesBody,
  ScheduleBody,
  SecurityBody,
  SettingsError,
  SetupBody,
  ThemeRow,
} from './settings-sections.js';

/** Moves focus to an element once it exists (a section that just opened or closed). */
function useFocusAfterRender(): (id: string) => void {
  const [target, setTarget] = useState<string | null>(null);
  useEffect(() => {
    if (!target) return;
    const element = document.getElementById(target);
    const focusable = element?.matches('button, a') ? element : element?.querySelector<HTMLElement>('button, a');
    focusable?.focus();
    setTarget(null);
  }, [target]);
  return setTarget;
}

function SummaryText({ parts }: { parts: SummaryPart[] }) {
  return (
    <>
      {parts.map((part, index) => (
        <span key={part.text} className={`sum-part sum-part--${part.tone}`}>
          {index > 0 ? <span className="sum-part__dot"> · </span> : null}
          {part.triangle ? <TriangleGlyph /> : null}
          {part.text}
        </span>
      ))}
    </>
  );
}

function SettingsBar({ searchOpen, onSearch }: { searchOpen: boolean; onSearch: () => void }) {
  return (
    <header className="detailbar">
      <Link to="/diger" className="detailbar__back">
        <Icon name="chevron-left" size={22} />
        Diğer
      </Link>
      <h1 className="detailbar__title">Ayarlar</h1>
      <button
        type="button"
        className="detailbar__icon"
        aria-label="Ayarlarda ara"
        aria-expanded={searchOpen}
        aria-controls="ayar-ara"
        onClick={onSearch}
      >
        <Icon name="search" size={20} />
      </button>
    </header>
  );
}

function SearchPanel({ settings }: { settings: Settings }) {
  const [query, setQuery] = useState('');
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    input.current?.focus();
  }, []);
  const results = searchSettings(settings, query);
  return (
    <search id="ayar-ara" className="st-search">
      <label className="st-search__field">
        <Icon name="search" size={16} className="ink-2" />
        <input
          ref={input}
          type="search"
          className="st-search__input"
          aria-label="Ayar veya bağlantı ara"
          placeholder="Ayar veya bağlantı ara"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>
      {query.trim() !== '' ? (
        results.length > 0 ? (
          <ul className="st-search__results" aria-label="Arama sonuçları">
            {results.map((result) => (
              <li key={result.id}>
                <Link to={result.to} className="more-row st-search__row">
                  <span className="more-row__title">
                    {result.label}
                    <span className="st-search__detail">{result.detail}</span>
                  </span>
                  <Icon name="chevron-right" size={18} className="ink-3" />
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="st-search__empty" role="status">
            Sonuç yok
          </p>
        )
      ) : null}
    </search>
  );
}

function SetupHead({ settings, summary }: { settings: Settings; summary: SetupSummary }) {
  const next = summary.next;
  return (
    <section className="setup-head drafting" aria-labelledby="kurulum-h">
      <h2 id="kurulum-h" className="setup-head__title" aria-live="polite">
        Kurulum: {summary.ready} / {summary.total} bağlantı hazır
      </h2>
      <SetupBar summary={summary} />
      <SetupLegend summary={summary} />
      {next ? (
        <Link to={`/ayarlar/baglanti/${next.id}`} className="next-card">
          <span className="next-card__bar" aria-hidden="true" />
          <span className="next-card__text">
            <span className="next-card__title">
              <span className="next-card__key">Sıradaki:</span> {next.setup?.title ?? next.name}
            </span>
            <span className="next-card__sub">
              {summary.nextIndex}. adım · {richToString(next.purpose)}
            </span>
          </span>
          <span className="next-card__go">
            Başla
            <Icon name="chevron-right" size={16} />
          </span>
        </Link>
      ) : (
        <p className="setup-head__done">Sende bekleyen kurulum adımı yok.</p>
      )}
      <span className="sr-only">{settings.connections.length} bağlantıdan kapalı olanlar sayılmaz.</span>
    </section>
  );
}

function PhoneConnectionRow({ connection, next }: { connection: Connection; next: boolean }) {
  const qualifier = richToString(connection.qualifier);
  const note = connection.alert && !isLive(connection) ? connection.alert.text : connection.phoneNote;
  return (
    <Link
      to={`/ayarlar/baglanti/${connection.id}`}
      className={`crow${next ? ' crow--next' : ''}${connection.status === 'disabled' ? ' crow--off' : ''}`}
      aria-label={`${connection.name}${qualifier ? `, ${qualifier}` : ''}. ${next ? 'Sıradaki adım. ' : ''}${
        STATUS_LABELS[connection.status]
      }: ${note}. Ayrıntı`}
    >
      {next ? <span className="crow__next-bar" aria-hidden="true" /> : null}
      <ServiceGlyph connection={connection} />
      <span className="crow__l1">
        <span className="crow__name">{connection.name}</span>
        {connection.qualifier.length > 0 ? (
          <span className="crow__qual">
            <Rich text={connection.qualifier} />
          </span>
        ) : null}
      </span>
      <span className={connection.alert && !isLive(connection) ? 'crow__l2 crow__l2--alert' : 'crow__l2'}>
        {next ? (
          <>
            <span className="key crow__next">Sıradaki</span> ·{' '}
          </>
        ) : null}
        {connection.alert && !isLive(connection) ? <TriangleGlyph /> : null}
        {note}
      </span>
      <span className="crow__pill">
        <ConnectionPill status={connection.status} />
      </span>
      <Icon name="chevron-right" size={18} className="crow__chev" />
    </Link>
  );
}

function GroupList({
  group,
  nextId,
  onCollapse,
}: {
  group: GroupSummary;
  nextId: string | null;
  onCollapse: () => void;
}) {
  return (
    <div className="grp" id={`grp-${group.id}`}>
      <h3 className="grp__head">
        <button type="button" className="grp__btn" aria-expanded="true" onClick={onCollapse}>
          <span className="key">
            {group.title} · {group.connections.length}
          </span>
          <span className="grp__sum">
            <SummaryText parts={group.parts.slice(0, 2)} />
          </span>
          <span className="sr-only">. Kapat</span>
        </button>
      </h3>
      {group.connections.map((connection) => (
        <PhoneConnectionRow key={connection.id} connection={connection} next={connection.id === nextId} />
      ))}
    </div>
  );
}

/** Title-block grid cells for collapsed groups or sections; an odd last cell spans the row. */
function TitleBlockGrid({ label, children }: { label?: string; children: ReactNode }) {
  return label ? (
    <nav className="tb-grid" aria-label={label}>
      {children}
    </nav>
  ) : (
    <div className="tb-grid">{children}</div>
  );
}

function Connections({ settings, summary }: { settings: Settings; summary: SetupSummary }) {
  const [open, setOpen] = useState(true);
  const [groupsOpen, setGroupsOpen] = useState<ReadonlySet<ConnectionGroupId>>(new Set(['core']));
  const focus = useFocusAfterRender();
  const groups = groupSummaries(settings);
  const expanded = groups.filter((group) => groupsOpen.has(group.id));
  const collapsed = groups.filter((group) => !groupsOpen.has(group.id));
  const toggle = (id: ConnectionGroupId, value: boolean) => {
    const nextSet = new Set(groupsOpen);
    if (value) nextSet.add(id);
    else nextSet.delete(id);
    setGroupsOpen(nextSet);
    focus(value ? `grp-${id}` : `cell-${id}`);
  };
  return (
    <section className="st-sec st-sec--first" aria-labelledby="bag-h">
      <SectionToggle
        id="bag-h"
        title="Bağlantılar"
        count={settings.connections.length}
        hint="sahibi Sercan · token ajanlarda değil"
        expanded={open}
        onToggle={() => setOpen(!open)}
      />
      <div id="bag-h-govde" hidden={!open}>
        {expanded.map((group) => (
          <GroupList
            key={group.id}
            group={group}
            nextId={summary.next?.id ?? null}
            onCollapse={() => toggle(group.id, false)}
          />
        ))}
        {collapsed.length > 0 ? (
          <TitleBlockGrid>
            {collapsed.map((group) => (
              <button
                key={group.id}
                id={`cell-${group.id}`}
                type="button"
                className="tb-cell"
                aria-expanded="false"
                aria-label={`${group.spoken}. Aç`}
                onClick={() => toggle(group.id, true)}
              >
                <span className="tb-cell__title tb-cell__title--small">
                  {group.title}
                  <span className="tb-cell__count mono">{group.connections.length}</span>
                  <Icon name="chevron-down" size={16} className="tb-cell__chev" />
                </span>
                <span className="tb-cell__sub">
                  <SummaryText parts={group.phoneParts} />
                </span>
              </button>
            ))}
          </TitleBlockGrid>
        ) : null}
      </div>
    </section>
  );
}

function Policy({ settings }: { settings: Settings }) {
  const [open, setOpen] = useState(true);
  const gm = policyById(settings, 'budget_gm_month');
  const cap = policyById(settings, 'budget_team_cap');
  const undo = policyById(settings, 'approval_undo');
  const ttl = policyById(settings, 'approval_ttl');
  const hold = policyById(settings, 'approval_t4_hold');
  const discount = policyById(settings, 'discount_cap');
  const suggestions = pendingSuggestions(settings).length;
  const budgetLine = `GM ${gm ? formatPolicyValue(gm, gm.value) : '-'} API karşılığı · ekip tavanı ${
    cap ? formatPolicyValue(cap, cap.value) : '-'
  }`;
  const timesLine = `T3 geri alma ${undo ? formatPolicyValue(undo, undo.value) : '-'} · onay ömrü ${
    ttl ? formatPolicyValue(ttl, ttl.value) : '-'
  } · T4 basılı ${hold ? formatPolicyValue(hold, hold.value) : '-'}`;
  return (
    <section className="st-sec" aria-labelledby="pol-h">
      <SectionToggle
        id="pol-h"
        title="Politika"
        hint="çalışırken değişir · her değişiklik kayıtlı"
        expanded={open}
        onToggle={() => setOpen(!open)}
      />
      <div id="pol-h-govde" hidden={!open}>
        <Link
          to="/ayarlar/butceler"
          className="pol-row pol-row--link"
          aria-label={`Bütçeler: ${budgetLine}.${suggestions > 0 ? ` ${suggestions} öneri kararını bekliyor.` : ''} Aç`}
        >
          <span className="pol-row__text">
            <span className="pol-row__title">Bütçeler</span>
            <span className="pol-row__sub">{budgetLine}</span>
          </span>
          {suggestions > 0 ? <span className="sug-badge">{suggestions} öneri</span> : null}
          <Icon name="chevron-right" size={18} className="ink-3" />
        </Link>
        <Link
          to="/ayarlar/onay-sureleri"
          className="pol-row pol-row--link"
          aria-label={`Onay süreleri: ${timesLine}. Aç`}
        >
          <span className="pol-row__text">
            <span className="pol-row__title">Onay süreleri</span>
            <span className="pol-row__sub">{timesLine}</span>
          </span>
          <Icon name="chevron-right" size={18} className="ink-3" />
        </Link>
        {discount ? <PolicyRow policy={discount} hint="artırmak: basılı tut + gerekçe" /> : null}
        <MailStageRow settings={settings} />
        <FlagsBlock settings={settings} />
      </div>
    </section>
  );
}

function HardLimitsSection({ settings }: { settings: Settings }) {
  const [open, setOpen] = useState(true);
  return (
    <section className="st-sec" aria-labelledby="sert-h">
      <SectionToggle
        id="sert-h"
        title="Sert sınırlar"
        lock
        hint={`kodda · salt okunur · ${settings.hardLimits.length} kural`}
        expanded={open}
        onToggle={() => setOpen(!open)}
      />
      <div id="sert-h-govde" className="hard-wrap" hidden={!open}>
        <HardLimits settings={settings} />
      </div>
    </section>
  );
}

function Preferences({ settings }: { settings: Settings }) {
  const [open, setOpen] = useState(true);
  const store = useStore();
  const n = settings.preferences.notifications;
  const telegram = settings.connections.find((item) => item.id === 'telegram');
  const botMissing = telegram ? !isLive(telegram) : false;
  const currency = settings.preferences.currency === 'code' ? '1.234,56 USD' : '1.234,56 $';
  return (
    <section className="st-sec" aria-labelledby="ter-h">
      <SectionToggle
        id="ter-h"
        title="Tercihler"
        hint="serbest · tek dokunuş, gerekçe yok"
        expanded={open}
        onToggle={() => setOpen(!open)}
      />
      <div id="ter-h-govde" hidden={!open}>
        <ThemeRow />
        <Link
          to="/ayarlar/bildirimler"
          className="pol-row pol-row--link pol-row--tall"
          aria-label={`Bildirimler: Telegram'dan anlık onaylar ve uyarılar, günlük rapor ${n.dailyReportAt}, haftalık rapor ${n.weeklyReportAt}.${
            botMissing ? ' Telegram botu henüz bağlı değil.' : ''
          } Aç`}
        >
          <span className="pol-row__text">
            <span className="pol-row__title">Bildirimler</span>
            <span className="pol-row__sub">Telegram anlık: onaylar, uyarılar</span>
            <span className="pol-row__sub">
              rapor {n.dailyReportAt} · haftalık Pzt {n.weeklyReportAt.split(' ').at(-1)}
            </span>
          </span>
          {botMissing ? <span className="sug-badge">bot bekleniyor</span> : null}
          <Icon name="chevron-right" size={18} className="ink-3" />
        </Link>
        <PrefSwitch
          label="Sessiz saatler"
          hint={
            <>
              {n.quietFrom}-{n.quietTo} · Telegram sessiz
            </>
          }
          checked={n.quietHours}
          onChange={(value) => void store.updatePreferences({ notifications: { quietHours: value } })}
        />
        <Link
          to="/ayarlar/dil"
          className="pol-row pol-row--link pol-row--pref"
          aria-label={`Dil ve bölge: Türkçe, saat dilimi ${settings.preferences.timezone}, para ${currency} biçiminde. Aç`}
        >
          <span className="pol-row__text">
            <span className="pol-row__title">Dil ve bölge</span>
            <span className="pol-row__sub">
              Türkçe · {settings.preferences.timezone} · {currency}
            </span>
          </span>
          <Icon name="chevron-right" size={18} className="ink-3" />
        </Link>
      </div>
    </section>
  );
}

type ExtraId = 'ajanlar' | 'takvim' | 'veri' | 'guvenlik' | 'hakkinda';

const EXTRA_ORDER: ExtraId[] = ['ajanlar', 'takvim', 'veri', 'guvenlik', 'hakkinda'];

function extraBody(id: ExtraId, settings: Settings): ReactNode {
  switch (id) {
    case 'ajanlar':
      return <AgentsBody settings={settings} />;
    case 'takvim':
      return <ScheduleBody settings={settings} />;
    case 'veri':
      return <DataBody settings={settings} />;
    case 'guvenlik':
      return <SecurityBody settings={settings} />;
    case 'hakkinda':
      return <AboutBody settings={settings} />;
  }
}

function extraCell(id: ExtraId, settings: Settings): { sub: ReactNode; spoken: string } {
  switch (id) {
    case 'ajanlar':
      return { sub: agentsSummary(settings), spoken: `Ajanlar: ${agentsSummary(settings)}` };
    case 'takvim':
      return {
        sub: `${settings.schedule.jobs.length} iş · ${settings.schedule.timezone}`,
        spoken: `Takvim: ${settings.schedule.jobs.map((job) => `${job.time} ${job.title}`).join(', ')}; ${settings.schedule.timezone}`,
      };
    case 'veri': {
      const audit = policyById(settings, 'retention_audit');
      const keep = audit ? formatPolicyValue(audit, audit.value) : '';
      return backupMissing(settings)
        ? {
            sub: (
              <>
                <span className="sum-part--danger">
                  <TriangleGlyph />
                  Yedek yok
                </span>{' '}
                · kayıt {keep}
              </>
            ),
            spoken: `Veri ve saklama: yedek yok. Denetim kaydı ${keep}`,
          }
        : { sub: `kayıt ${keep}`, spoken: `Veri ve saklama: denetim kaydı ${keep}` };
    }
    case 'guvenlik':
      return killSwitchUntested(settings)
        ? {
            sub: (
              <span className="warn-chip">
                <TriangleGlyph />
                Durdurma denenmedi
              </span>
            ),
            spoken: `Güvenlik: acil durdurma hiç denenmedi. Oturum ${settings.security.session.method}`,
          }
        : { sub: settings.security.session.method, spoken: `Güvenlik: oturum ${settings.security.session.method}` };
    case 'hakkinda':
      return {
        sub: (
          <>
            <span className="mono">{settings.about.version}</span> · CI{' '}
            {settings.about.ci === 'passed' ? 'geçti' : settings.about.ci === 'failed' ? 'kırmızı' : 'çalışıyor'}
          </>
        ),
        spoken: `Hakkında: sürüm ${settings.about.version}, commit ${settings.about.commit}, CI ${
          settings.about.ci === 'passed' ? 'geçti' : settings.about.ci
        }`,
      };
  }
}

function MoreSections({ settings }: { settings: Settings }) {
  const [open, setOpen] = useState<ReadonlySet<ExtraId>>(new Set());
  const focus = useFocusAfterRender();
  const toggle = (id: ExtraId, value: boolean) => {
    const next = new Set(open);
    if (value) next.add(id);
    else next.delete(id);
    setOpen(next);
    focus(value ? `ex-${id}-h` : `cell-ex-${id}`);
  };
  const expanded = EXTRA_ORDER.filter((id) => open.has(id));
  const collapsed = EXTRA_ORDER.filter((id) => !open.has(id));
  return (
    <>
      {expanded.map((id) => (
        <section key={id} className="st-sec" aria-labelledby={`ex-${id}-h`}>
          <SectionToggle
            id={`ex-${id}-h`}
            title={TAB_TITLES[id]}
            hint={id === 'hakkinda' ? settings.about.repo : ''}
            expanded
            onToggle={() => toggle(id, false)}
          />
          <div id={`ex-${id}-h-govde`}>{extraBody(id, settings)}</div>
        </section>
      ))}
      <TitleBlockGrid label="Diğer ayar bölümleri">
        {collapsed.map((id) => {
          const cell = extraCell(id, settings);
          return (
            <button
              key={id}
              id={`cell-ex-${id}`}
              type="button"
              className="tb-cell"
              aria-expanded="false"
              aria-label={`${cell.spoken}. Aç`}
              onClick={() => toggle(id, true)}
            >
              <span className="tb-cell__title">
                {TAB_TITLES[id]}
                <Icon name="chevron-down" size={16} className="tb-cell__chev" />
              </span>
              <span className="tb-cell__sub">{cell.sub}</span>
            </button>
          );
        })}
        <Link
          to="/ayarlar/api-kullanimi"
          className="tb-cell"
          aria-label="API kullanımı: bütün API’lerin bugünkü ve bu ayki kullanımı, kalanları ve son 30 günü"
        >
          <span className="tb-cell__title">
            API kullanımı
            <Icon name="chevron-right" size={16} className="tb-cell__chev" />
          </span>
          <span className="tb-cell__sub">bugün, bu ay, kalan</span>
        </Link>
        <Link
          to="/ayarlar/degisiklikler"
          className="tb-cell"
          aria-label="Değişiklik kaydı: politika ve bağlantı değişiklikleri, kim, ne zaman, gerekçe"
        >
          <span className="tb-cell__title">
            Değişiklik kaydı
            <Icon name="chevron-right" size={16} className="tb-cell__chev" />
          </span>
          <span className="tb-cell__sub">kim, ne zaman, gerekçe</span>
        </Link>
      </TitleBlockGrid>
    </>
  );
}

/** Stale data or a stopped team still shows its strip here; live and running shows nothing. */
function QuietStatus() {
  const { connection, team } = usePanel();
  return connection === 'stale' || team.mode !== 'running' ? <StatusArea /> : null;
}

function AyarlarList() {
  const state = usePanel();
  const [searchOpen, setSearchOpen] = useState(false);
  const settings = state.settings;
  const summary = settings ? setupSummary(settings) : null;
  return (
    <>
      <SettingsBar searchOpen={searchOpen} onSearch={() => setSearchOpen(!searchOpen)} />
      <QuietStatus />
      {settings && searchOpen ? <SearchPanel settings={settings} /> : null}
      {settings && summary ? (
        <>
          <SetupHead settings={settings} summary={summary} />
          <main className="phone-main st-main">
            <Connections settings={settings} summary={summary} />
            <Policy settings={settings} />
            <HardLimitsSection settings={settings} />
            <Preferences settings={settings} />
            <MoreSections settings={settings} />
          </main>
        </>
      ) : (
        <main className="phone-main">
          <SettingsError />
        </main>
      )}
      <BottomTabBar active="diger" />
    </>
  );
}

export function SubscreenBody({ tab, settings }: { tab: SettingsTab; settings: Settings }) {
  switch (tab) {
    case 'kurulum':
      return <SetupBody settings={settings} />;
    case 'baglantilar':
      return <AllConnections settings={settings} />;
    case 'api-kullanimi':
      return <ApiUsageBody />;
    case 'politika':
      return <PolicyBody settings={settings} />;
    case 'butceler':
      return <BudgetsBody settings={settings} />;
    case 'onay-sureleri':
      return <ApprovalTimesBody settings={settings} />;
    case 'sert-sinirlar':
      return (
        <div className="hard-wrap">
          <HardLimits settings={settings} showSource />
        </div>
      );
    case 'ajanlar':
      return <AgentsBody settings={settings} />;
    case 'takvim':
      return <ScheduleBody settings={settings} />;
    case 'tercihler':
      return <PreferencesBody settings={settings} />;
    case 'bildirimler':
      return <NotificationsBody settings={settings} />;
    case 'dil':
      return <LanguageBody settings={settings} />;
    case 'veri':
      return <DataBody settings={settings} />;
    case 'guvenlik':
      return <SecurityBody settings={settings} />;
    case 'hakkinda':
      return <AboutBody settings={settings} />;
    case 'degisiklikler':
      return <ChangeLogBody settings={settings} />;
  }
}

function AllConnections({ settings }: { settings: Settings }) {
  const summary = setupSummary(settings);
  return (
    <>
      {groupSummaries(settings).map((group) => (
        <div key={group.id} className="grp">
          <h2 className="grp__head">
            <span className="grp__btn">
              <span className="key">
                {group.title} · {group.connections.length}
              </span>
              <span className="grp__sum">
                <SummaryText parts={group.parts} />
              </span>
            </span>
          </h2>
          {group.connections.map((connection) => (
            <PhoneConnectionRow key={connection.id} connection={connection} next={connection.id === summary.next?.id} />
          ))}
        </div>
      ))}
    </>
  );
}

function AyarlarSubscreen({ tab }: { tab: SettingsTab }) {
  const state = usePanel();
  const settings = state.settings;
  const summary = settings ? setupSummary(settings) : null;
  return (
    <>
      <DetailAppBar
        back={{ label: 'Ayarlar', to: '/ayarlar' }}
        title={TAB_TITLES[tab]}
        titleMono={null}
        segments={null}
        metaKey="Kurulum"
        metaValue={summary ? `${summary.ready} / ${summary.total}` : '-'}
      />
      <QuietStatus />
      <main className="phone-main st-main st-main--sub">
        <h1 className="sr-only">{TAB_TITLES[tab]}</h1>
        {tab === 'api-kullanimi' ? (
          // v0.1.164: its own read (GET /settings/api-usage), shown without the settings model too.
          <ApiUsageBody />
        ) : settings ? (
          <SubscreenBody tab={tab} settings={settings} />
        ) : (
          <SettingsError />
        )}
      </main>
      <BottomTabBar active="diger" />
    </>
  );
}

export function Ayarlar({ tab }: { tab: SettingsTab | null }) {
  return tab === null ? <AyarlarList /> : <AyarlarSubscreen tab={tab} />;
}
